/**
 * Por qué cambió tu dinero disponible un mes más (o menos) que tu ahorro: «traspasos y otros», movimiento a movimiento (puro).
 * Cada movimiento mueve tu liquidez (importe × tu parte de la cuenta, si la cuenta es liquidez y es de ese mes) y tu ahorro
 * (lo atribuible, en el mes en que cuenta). La diferencia de cada uno, agrupada por el motivo, suma exactamente
 * «traspasos y otros»; lo que no sale de movimientos (un saldo apuntado a mano en Patrimonio) va aparte.
 */
import { round2 } from '@pf/domain';
import { isInternalTransfer, type Computed } from './model.js';
import { accountUse } from './account-use.js';

export type BreakdownKey = 'reserved' | 'reservedPay' | 'transfer' | 'shared' | 'excluded' | 'shiftIn' | 'shiftOut' | 'outside' | 'other';
/**
 * moved: mes en que se movió el dinero; counts: mes en que cuenta como ingreso o gasto (solo «cuentan en otro mes»).
 * dest: en un traspaso, la cuenta del otro lado (aunque no sea dinero disponible); vacío si no está en el libro.
 */
export interface BreakdownItem { id: string; date: string; concept: string; account: string; amount: number; effect: number; why: string; category: string; moved: string; counts: string; dest: string }
export interface BreakdownGroup { key: BreakdownKey; label: string; note: string; total: number; items: BreakdownItem[] }
export interface Breakdown {
  groups: BreakdownGroup[];
  /** Todos los grupos con todos sus movimientos, también los que suman 0 en el mes (para sumar el año movimiento a movimiento). */
  all: BreakdownGroup[];
  /** Lo aportado a fondos (sale arriba como «a fondos»): se compensa aquí para que el total cuadre. */
  invested: number;
  /** Diferencia con el saldo apuntado en Patrimonio (no sale de movimientos). */
  residual: number;
  total: number;
}

const LABEL: Record<BreakdownKey, [string, string]> = {
  reserved: ['Lo que apartas para pagos', 'Dinero que pasas a una cuenta apartada para pagos (o que vuelve de ella): sigue siendo tuyo, pero ya no está en tu dinero disponible.'],
  reservedPay: ['Pagos desde cuentas apartadas', 'Gastos tuyos pagados desde la cuenta apartada: cuentan en tu ahorro, pero no salen de tu dinero disponible (salieron al apartarlo).'],
  transfer: ['Traspasos entre tus cuentas', 'De lo que pasas a una cuenta compartida solo es tuyo tu parte: el resto ya es de la otra persona.'],
  shared: ['Gastos compartidos que pagaste enteros', 'Salió el gasto entero de tu cuenta, pero tu gasto es solo tu parte (o te devolvieron la suya). Lo de Tricount está en «te debe».'],
  excluded: ['Fondos, ajustes y saldos', 'Movimientos que no son ingreso ni gasto: lo que pasas a fondos, ajustes, saldos de Tricount…'],
  shiftIn: ['Cobros que cuentan en otro mes', 'Lo que cobras a final de mes (como la nómina) cuenta para el mes siguiente: este mes entra lo del que viene, y lo de este mes ya entró el anterior.'],
  shiftOut: ['Pagos que cuentan en otro mes', 'Pagos que salen de tu cuenta un mes pero cuentan como gasto de otro (p. ej. la hipoteca cobrada a final de mes).'],
  outside: ['Cuentas que no son dinero disponible', 'Ingresos o gastos de cuentas que no cuentan como dinero disponible (p. ej. inversión).'],
  other: ['Otros ajustes', 'Por ejemplo, gastos de una cuenta compartida sin marcar como compartidos.'],
};

const countable = (kind: string) => kind === 'income' || kind === 'expense';
const MONTH_WORDS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
export const monthWord = (m: string) => (m ? MONTH_WORDS[Number(m.slice(5, 7)) - 1] ?? m : '');

export function liquidityBreakdown(c: Computed, month: string, today: string, other: number, invested: number): Breakdown {
  const current = today.slice(0, 7);
  const until = month === current ? today : `${month}-31`;
  const accounts = new Map(c.wb.accounts.filter(a => a.inNetWorth && accountUse(a).use !== 'reserved').map(a => [a.name, a.share || 1]));
  const reserved = new Set(c.wb.accounts.filter(a => a.inNetWorth && accountUse(a).use === 'reserved').map(a => a.name));
  // Traspasos con una cuenta apartada: el otro lado (mismo día, importe contrario) está en una de ellas
  const reservedPeer = (m: { account: string; date: string; amount: number }) =>
    c.movements.find(x => reserved.has(x.account) && x.date === m.date && Math.abs(x.amount + m.amount) < 0.005)?.account;
  const toReserved = (m: { account: string; date: string; amount: number }) => !!reservedPeer(m);
  // El otro lado de un traspaso en cualquier cuenta del libro (hasta 3 días después o antes: las transferencias tardan)
  const near = (a: string, b: string) => Math.abs(Date.parse(a) - Date.parse(b)) <= 3 * 86400000;
  const peerOf = (m: { account: string; date: string; amount: number }) =>
    c.movements.find(x => x.account !== m.account && !!x.date && near(x.date, m.date) && Math.abs(x.amount + m.amount) < 0.005)?.account ?? '';
  const groups = new Map<BreakdownKey, BreakdownItem[]>();
  for (const m of c.movements) {
    const parts = c.partsBy.get(m.id) ?? [];
    const inMonth = !!m.date && m.date.slice(0, 7) === month && m.date <= until;
    const counts = m.effMonth === month || parts.some(p => p.effMonth === month);
    if (!inMonth && !counts) continue;
    const share = accounts.get(m.account);
    const cash = inMonth && share !== undefined ? m.amount * share : 0;
    const saving = (m.effMonth === month && countable(m.kind) ? m.attributable : 0)
      + parts.filter(p => p.effMonth === month && countable(p.kind)).reduce((t, p) => t + p.attributable, 0);
    const effect = round2(cash - saving);
    if (Math.abs(effect) < 0.005) continue;
    const shift = inMonth !== counts || (parts.length && parts.some(p => p.effMonth !== m.date.slice(0, 7)));
    const key: BreakdownKey = shift ? (m.amount > 0 ? 'shiftIn' : 'shiftOut')
      : share === undefined ? (reserved.has(m.account) ? (isInternalTransfer(m.effCategory) ? 'reserved' : 'reservedPay') : 'outside')
      : isInternalTransfer(m.effCategory) ? (toReserved(m) ? 'reserved' : 'transfer')
      : !countable(m.kind) && !parts.length ? 'excluded'
      : m.effShared ? 'shared' : 'other';
    const moved = m.date.slice(0, 7), countsIn = shift ? (inMonth ? m.effMonth || parts[0]?.effMonth || '' : month) : '';
    const verb = m.amount > 0 ? 'entró' : 'salió';
    const why = !shift ? '' : inMonth ? `${verb} en ${monthWord(month)}, pero es de ${monthWord(countsIn)}` : `es de ${monthWord(month)}, pero ${verb} en ${monthWord(moved)}`;
    const peer = key === 'reserved' && share !== undefined ? reservedPeer(m) : undefined;
    const account = peer ? (m.amount < 0 ? `${m.account} → ${peer}` : `${peer} → ${m.account}`) : m.account;
    (groups.get(key) ?? groups.set(key, []).get(key)!).push({ id: m.id, date: m.date, concept: m.concept, account, amount: m.amount, effect, why,
      category: m.effCategory || parts[0]?.category || '', moved, counts: countsIn,
      dest: key === 'reserved' ? reservedPeer(m) ?? '' : key === 'transfer' ? peerOf(m) : '' });
  }
  // Un traspaso son dos movimientos (sale de una cuenta, entra en otra): se enseña como uno, con lo que te mueve de verdad
  const transfers = groups.get('transfer');
  if (transfers) {
    const used = new Set<BreakdownItem>(), merged: BreakdownItem[] = [];
    for (const out of transfers.filter(x => x.amount < 0)) {
      const inn = transfers.find(x => !used.has(x) && x.amount > 0 && x.date === out.date && Math.abs(x.amount + out.amount) < 0.005);
      if (!inn) continue;
      used.add(out); used.add(inn);
      merged.push({ ...out, account: `${out.account} → ${inn.account}`, effect: round2(out.effect + inn.effect), dest: inn.account });
    }
    groups.set('transfer', [...merged, ...transfers.filter(x => !used.has(x))]);
  }
  const all = [...groups.entries()].map(([key, items]): BreakdownGroup => ({
    key, note: LABEL[key][1],
    // Con una sola cuenta apartada, su nombre: «A Laboral Kutxa», «Pagos desde Laboral Kutxa»
    label: reserved.size === 1 && key === 'reserved' ? `A ${[...reserved][0]}` : reserved.size === 1 && key === 'reservedPay' ? `Pagos desde ${[...reserved][0]}` : LABEL[key][0],
    total: round2(items.reduce((t, x) => t + x.effect, 0)),
    items,
  }));
  // Los céntimos de redondeo (p. ej. la mitad de un gasto compartido de una cuenta compartida) no se enseñan
  const list = all.map(g => ({ ...g, items: g.items.filter(x => Math.abs(x.effect) >= 0.5).sort((a, b) => Math.abs(b.effect) - Math.abs(a.effect)) }));
  const fromMovements = round2(list.reduce((t, g) => t + g.total, 0));
  const shown = list.filter(g => Math.abs(g.total) >= 0.5 && g.items.length).sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  return { groups: shown, all, invested, residual: round2(other - fromMovements - invested), total: other };
}
